import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ILibDropdownItem, LanguageSelector } from '@infologistics/frontend-libraries'

import { useAppDispatch, useAppSelector } from '@hooks'
import LocalStorageService from '@services/localStorage'
import { LanguageShort, Instance } from '@const/consts'
import { getPreferredLanguages } from '@utils/utils'
import { setIsOpenedMenu } from '@store/modules/startup'
import { ILanguageNames } from './types'

import styles from './LanguageSelectorWrapper.module.css'
import { setLanguage } from '@app/store/modules/utils'
import { Language } from '@app/const/translations'
import { RootState } from '@app/store/types'

const LanguageSelectorWrapper: FC = () => {
  const { language: savedLanguage } = useAppSelector((state) => state.utils)
  const {
    i18n: {
      changeLanguage,
      language,
      languages,
    }, t,
  } = useTranslation('language')

  const instanceFromStore = useAppSelector((state) => state.utils.instance)
  const instance = LocalStorageService.getInstance() ?? instanceFromStore ?? Instance.GLOBAL;

  const languageFromState = useAppSelector((state: RootState) => state.utils.language);

  const { isOpenedMenu } = useAppSelector(state => state.app)
  const dispatch = useAppDispatch()
  const [languageNames, setLanguageNames] = useState<Record<string, string>>({})
  const [languageNamesShort, setLanguageNamesShort] = useState<Record<string, string>>({})

  const getLanguageNames = (languages: readonly string[]): ILanguageNames => (
    languages.reduce(
      (acc, language) => ({
        ...acc,
        languageNames: {
          ...acc.languageNames,
          [language]: t(Language[language]),
        },
        languageNamesShort: {
          ...acc.languageNamesShort,
          [language]: t(LanguageShort[language]),
        },
      }),
      {
        languageNames: {},
        languageNamesShort: {},
      },
    )
  )

  const setStateData = (): void => {
    const { languageNames, languageNamesShort } = getLanguageNames(languages)

    setLanguageNames(languageNames)
    setLanguageNamesShort(languageNamesShort)
  }

  useEffect(setStateData, [language, savedLanguage])

  const handleLanguageChange = (item: ILibDropdownItem): Promise<void> => {
    const { name } = item

    if (isOpenedMenu) dispatch(setIsOpenedMenu(false))

    return changeLanguage(name).then(() => {
      LocalStorageService.setLanguage(name)
      dispatch(setLanguage(name))
    })
  }

  return (
    <div className={styles.language_selector}>
      <LanguageSelector
        isThemeMini={true}
        language={languageFromState}
        languages={getPreferredLanguages(instance, languageFromState)}
        languageNames={languageNames}
        languageNamesShort={languageNamesShort}
        onLanguageChange={handleLanguageChange}
      />
    </div>
  )
}

export default LanguageSelectorWrapper
